/**
 * Marathi letter tracing — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tracing`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const marathiLetterTracingPack: ToilePack = {
    slug: 'marathi-letter-tracing',
    category: 'classroom',
    name: 'Marathi letter tracing',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'swar', label: 'Swar', subject: 'a Marathi tracing page of the swar "अ आ इ ई उ ऊ ए ऐ ओ औ अं अः" in dotted Devanagari on four-line rows, each letter hanging from its shirorekha'},
        {key: 'vyanjan', label: 'Vyanjan', subject: 'a Marathi tracing page of the last vyanjan "श ष स ह ळ क्ष ज्ञ" in a grid of boxes, each letter solid once then dotted twice beside it'},
        {key: 'barakhadi', label: 'Barakhadi', subject: 'a Marathi barakhadi tracing page for म, "म मा मि मी मु मू मे मै मो मौ मं मः" in a grid of boxes, each form dotted for tracing'},
    ],
    styles: [
        {key: 'print', label: 'Clean print', style: 'a clean school-printable worksheet with thin black ruled lines, uniform stroke weight and light grey dotted tracing', palette: 'black and grey ink on white paper with one soft accent colour only where the page names one', framing: 'the whole page used inside an even white margin'},
        {key: 'nursery', label: 'Nursery colour', style: 'a bright nursery-class worksheet with rounded friendly shapes, pastel boxes and a coloured heading band', palette: 'sky blue, sunshine yellow, coral and mint pastels with grey dotted tracing', framing: 'a coloured heading band across the top and the rest inside an even margin'},
        {key: 'notebook', label: 'Notebook paper', style: 'a pencil-on-notebook-paper worksheet with faint blue ruled lines and soft graphite tracing on warm cream paper', palette: 'cream paper, pale blue rules and graphite grey', framing: 'the whole page used inside a clear margin with a faint margin rule down the left'},
    ],
    directions: ['fruit words', 'bigger letters', 'saffron theme'],
    directionHint: 'try: fruit words',
    seo: {
        title: 'Marathi letter tracing: swar, vyanjan, barakhadi',
        metaDescription: 'Nine printable Marathi letter tracing pages in Devanagari: the swar, the vyanjan including ळ, and the barakhadi of one consonant.',
        h1: 'Marathi letter tracing: swar, vyanjan with ळ, and barakhadi',
        tagline: 'Three Marathi readings in clean print, nursery colour and notebook paper styles.',
        intro: 'Marathi letter tracing pages teach the mulakshare in Devanagari, at home or in balwadi and class 1. One page sets the swar, the vowels from अ to अः; another has the last vyanjan, श ष स ह ळ क्ष ज्ञ, with the Marathi ळ among them; a third is the barakhadi of म, with each vowel sign. Type a theme, such as fruit words, and the same three styles carry it.',
        useCases: [
            'a balwadi class',
            'class 1 reading practice at home',
            'a sheet for the Marathi ळ',
            'a barakhadi chart on a classroom wall',
        ],
        faq: [
            {q: 'Why does one page include ळ?', a: 'The Marathi ळ is shown among the last consonants of the vyanjan page.'},
            {q: 'What is the barakhadi?', a: 'One consonant written with each vowel sign. The sample page is म, with मा, मि, मी and the rest.'},
            {q: 'Can I ask for another letter?', a: 'Yes. Type it as a direction. It is a request, and the samples stay as shown.'},
        ],
    },
}
